import { DataTableSearch } from '@/common/ui/table/data-table-search'
import { DataTableProcessorSelector } from '@/common/ui/table/data-table-processor-selector'
import { DataTableStatusSelector } from '@/common/ui/table/data-table-status-selector'
import { columns } from './constants'
import { DepositTransactionResponse } from '../../types'
import { TransactionProcessor, DepositStatus } from '@/schema'
import { Button } from '@/common/ui/button'
import { Icons } from '@/common/ui/icons'
import { RefetchOptions } from '@tanstack/react-query'
import { useQueryState } from 'nuqs'
import { useMemo } from 'react'
import { DataTableResetFilter } from '@/common/ui/table/data-table-reset-filter'
import { DataTableArrow } from '@/common/ui/table/data-table-arrow'
import BlacklistLegend from '@/common/components/BlacklistLegend'
import useQueryGetPaymentGateway from '@/modules/bank/api/hooks/useQueryGetPaymentGateway'
import { Combobox } from '@/common/ui/combobox'

interface DepositTableProps {
	data: DepositTransactionResponse['data']
	total: number
	onRefresh: (options?: RefetchOptions) => Promise<unknown>
	isLoading?: boolean
	onSort?: (columnId: string, order: 'asc' | 'desc' | undefined) => void
}

const processorOptions = [
	{
		label: 'ทั้งหมด',
		value: '',
	},
	{
		label: 'ฝากด้วยพนักงาน',
		value: TransactionProcessor.MANUAL,
	},
	{
		label: 'ฝากอัตโนมัติ',
		value: TransactionProcessor.AUTO,
	},
]

const statusOptions = [
	{
		label: 'สร้างแล้ว',
		value: DepositStatus.CREATED,
	},
	{
		label: 'รอดำเนินการ',
		value: DepositStatus.PENDING,
	},
	{
		label: 'กำลังตรวจสอบ',
		value: DepositStatus.PROCESSING,
	},
	{
		label: 'สำเร็จ',
		value: DepositStatus.COMPLETED,
	},
	{
		label: 'รอ Payment Gateway ตรวจสอบ',
		value: DepositStatus.MANUAL_COMPLETED,
	},
	{
		label: 'Payment Gateway ล้มเหลว',
		value: DepositStatus.PAYMENT_GATEWAY_FAILED,
	},
	{
		label: 'ไม่สำเร็จ',
		value: DepositStatus.FAILED,
	},
	{
		label: 'ยกเลิก',
		value: DepositStatus.CANCELED,
	},
	{
		label: 'หมดเวลา',
		value: DepositStatus.EXPIRED,
	},
]

const defaultStatusValues = [
	DepositStatus.CREATED,
	DepositStatus.PENDING,
	DepositStatus.PROCESSING,
]

export default function DepositTable({
	data,
	total,
	onRefresh,
	isLoading,
	onSort,
}: DepositTableProps) {
	const [paymentGatewayId, setPaymentGatewayId] =
		useQueryState('paymentGatewayId')

	const { data: paymentGatewaysData } = useQueryGetPaymentGateway({
		skip: 0,
		take: 1000,
	})

	const paymentGatewayOptions = useMemo(
		() => [
			{ label: 'ทั้งหมด ', value: '' },
			...(paymentGatewaysData?.data
				.filter((pg) => pg.bankProviderName)
				.map((pg) => ({
					label: pg.bankProviderName,
					value: pg.id,
				})) || []),
		],
		[paymentGatewaysData],
	)

	return (
		<div className="space-y-4">
			{/* Filter Controls - Mobile Responsive */}
			<BlacklistLegend />
			<div className="space-y-4">
				{/* Filter Controls Row */}
				<div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
					<DataTableSearch
						placeHolder="ค้นหา..."
						searchHint="สามารถค้นหาด้วย: เบอร์โทร, เลขบัญชี, ชื่อบัญชีธนาคาร, รหัสธุรกรรม"
					/>

					<DataTableResetFilter />

					<Combobox
						options={paymentGatewayOptions}
						placeholder="เลือกบัญชีรับเงิน"
						value={paymentGatewayId ?? undefined}
						onValueChange={(val) => setPaymentGatewayId(val || null)}
					/>

					<DataTableProcessorSelector
						options={processorOptions}
						label="ประเภทการฝาก"
						paramKey="processBy"
					/>

					<Button
						variant="outline"
						size="sm"
						onClick={() => onRefresh()}
						title="รีเฟรชข้อมูล"
						className="h-8 w-8 sm:h-9 sm:w-9"
					>
						<Icons.refresh className="h-4 w-4" />
					</Button>
				</div>
			</div>

			{/* Status Filter - Full Width */}
			<div className="w-full">
				<DataTableStatusSelector
					options={statusOptions}
					label="สถานะ"
					paramKey="status"
					defaultValues={defaultStatusValues}
				/>
			</div>

			{/* Data Table */}
			<DataTableArrow
				columns={columns}
				data={data}
				total={total}
				isLoading={isLoading}
				onSort={onSort}
				getRowClassName={(row) =>
					row.isBlacklist
						? 'bg-red-100 border-l-4 border-l-red-600 hover:bg-red-200 text-red-900 font-medium shadow-sm ring-1 ring-red-200'
						: ''
				}
			/>
		</div>
	)
}
